import axios from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { apiBase } from './apiBase';

// Ejar -> TARS automation, served by the same Python engine as the fines API.
// Same base URL and the same Firebase bearer token as finesService: the engine only
// answers signed-in yelo users, and the token never appears in a URL.
const BASE = apiBase();   // the build's address, or /api on yelodubai.com (services/apiBase)
const http = axios.create({ baseURL: `${BASE}/automation`, timeout: 180000 });

// Firebase restores the signed-in user asynchronously, so `auth.currentUser` is still
// null for the first moments after a reload. A page that calls on mount therefore sent
// no token at all and got a 401 back - which the browser then hid behind a bare
// "Network Error". Waiting for that first resolution costs nothing and removes the
// whole class of "it fails only on the first load" reports.
const authReady = auth.authStateReady
    ? auth.authStateReady()
    : new Promise((resolve) => {
        const stop = onAuthStateChanged(auth, () => { stop(); resolve(); });
    });

http.interceptors.request.use(async (config) => {
    await authReady;
    const user = auth.currentUser;
    if (user) config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    return config;
});

const get = (url) => http.get(url).then((r) => r.data);

/** The engine's own message when it refuses, rather than axios's "Request failed with 403". */
const readable = (err) => {
    const detail = err?.response?.data?.detail;
    if (!err?.response && err?.message === 'Network Error') {
        return new Error('could not reach the engine - check you are signed in to yelo, '
            + 'then that the engine is up');
    }
    if (detail) return new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    if (err?.code === 'ECONNABORTED') return new Error('the engine took too long to answer');
    return err instanceof Error ? err : new Error(String(err));
};

export const automationService = {
    baseUrl: `${BASE}/automation`,

    /** `{ok, db, tables, cases, ocr:{packages, binary, usable, languages}}`. */
    health: () => get('/health').catch((e) => { throw readable(e); }),

    /**
     * Read one document. The file is sent exactly as it is — nothing is resized in the
     * browser, because the engine's quality gate has to judge the real picture the
     * employee took rather than a version this page shrank first.
     */
    extract: async (file, { kind, issuer } = {}) => {
        const form = new FormData();
        form.append('file', file, file.name);
        if (kind) form.append('kind', kind);
        if (issuer) form.append('issuer', issuer);
        try {
            const r = await http.post('/ocr/extract', form);
            return r.data;
        } catch (e) { throw readable(e); }
    },

    settings: () => get('/settings').catch((e) => { throw readable(e); }),

    openCases: (mine = false) => get(`/cases/open${mine ? '?mine=1' : ''}`)
        .catch((e) => { throw readable(e); }),

    createCase: (body) => http.post('/cases', body).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    patchCase: (id, body) => http.patch(`/cases/${encodeURIComponent(id)}`, body)
        .then((r) => r.data).catch((e) => { throw readable(e); }),

    caseByIdentity: (identity) => get(`/cases/by-identity/${encodeURIComponent(identity)}`)
        .catch((e) => { throw readable(e); }),

    // -- Ejar: prepare only -------------------------------------------------------
    // Neither of these can save. `ejarPreview` builds the body and hands it back; the
    // engine has no send call at all yet, by design.
    ejarLookups: () => get('/ejar/lookups').catch((e) => { throw readable(e); }),

    ejarPreview: (values) => http.post('/ejar/preview', values).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    /**
     * Before anything is filled (owner, 10 Oct 2026): is the customer already on Ejar, and what Ejar would refuse
     * by its own live rules (age, visa with a passport, number shapes, expiry). Reads only.
     * -> `{existing, blockers, warnings, rules}`.
     */
    ejarCheck: (values, files = []) => http.post('/ejar/check', {
        ...values, files: files.map((f) => ({ name: f.name, size: f.size })),
    }).then((r) => r.data).catch((e) => { throw readable(e); }),

    /**
     * The only call in this file that writes to Ejar. Nothing calls it but a button.
     *
     * The scans travel with it: the browser still holds the files the employee dropped,
     * and the engine forwards them to Ejar's store without keeping a copy. Sending them
     * again here is what lets us hold to "no document image is stored".
     */
    ejarCreate: (values, files = []) => {
        const form = new FormData();
        form.append('values', JSON.stringify(values));
        files.forEach((f) => form.append('files', f, f.name));
        return http.post('/ejar/create', form).then((r) => r.data)
            .catch((e) => { throw readable(e); });
    },

    /**
     * Fill Ejar's own screen and stop there.
     *
     * Ejar's API refuses everything we build with a bare 500, while the same customer
     * typed into its screen is accepted, so the engine drives that screen instead. It
     * fills the form and takes a picture; nothing is saved until `ejarFormSave`, which
     * only a person can press.
     *
     * The scan travels along because Ejar will not take a document without one. It is
     * held for the length of the call and kept nowhere.
     */
    ejarFormStart: (values, files = []) => {
        const form = new FormData();
        form.append('values', JSON.stringify(values));
        // One scan per document row, in the order the rows are filed.
        files.forEach((f) => form.append('files', f, f.name));
        return http.post('/ejar/form/start', form).then((r) => r.data)
            .catch((e) => { throw readable(e); });
    },

    /**
     * Open Ejar before anyone asks for it.
     *
     * Ejar shows nothing for about half a minute after a page load, and that half minute
     * used to land on the employee after they pressed the button. Spending it while they
     * are still reading the documents costs nobody anything.
     */
    ejarFormWarm: () => http.post('/ejar/form/warm').then((r) => r.data)
        .catch(() => null),          // a cold browser still works, just slowly

    // What the agent is doing this second. Cheap enough to ask while it works.
    ejarFormProgress: (job) => get(`/ejar/form/${encodeURIComponent(job)}/progress`)
        .catch(() => null),

    ejarFormSave: (job) => http.post(`/ejar/form/${encodeURIComponent(job)}/save`)
        .then((r) => r.data).catch((e) => { throw readable(e); }),

    ejarFormCancel: (job) => http.post(`/ejar/form/${encodeURIComponent(job)}/cancel`)
        .then((r) => r.data).catch((e) => { throw readable(e); }),

    /**
     * A customer Ejar already has (owner, 12 Oct 2026): what the new papers would change on his profile - the
     * papers running later than Ejar's, the expired Work Cards. Reads only.
     * -> `{changes, deletes, notes, up_to_date, customer_id}`.
     */
    ejarUpdatePlan: (values) => http.post('/ejar/update/plan', values).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    /**
     * Fill Ejar's edit screen with the new papers and stop there: the same job as `ejarFormStart` - watched with
     * `ejarFormProgress`, saved only by `ejarFormSave` (a person's press). Each scan travels with what it is
     * (`kinds`: "id" / "passport" / "licence"), and the engine picks each document's scan by that, never by place.
     */
    ejarUpdateStart: (values, papers = []) => {
        const form = new FormData();
        form.append('values', JSON.stringify(values));
        form.append('kinds', JSON.stringify(papers.map((p) => p.kind || null)));
        papers.forEach((p) => form.append('files', p.file, p.file.name));
        return http.post('/ejar/update/start', form).then((r) => r.data)
            .catch((e) => { throw readable(e); });
    },

    /** The customers saved on Ejar from this page on one day (default today, Dubai): `{day, rows}`. Reads only. */
    ejarRegistrations: (day = '') => get(`/ejar/registrations${day ? `?day=${encodeURIComponent(day)}` : ''}`)
        .catch((e) => { throw readable(e); }),

    // -- Administration: deleting a customer --------------------------------------
    // The engine decides who may call these by the signed-in address, not by a role.
    // Nothing here deletes without a lookup first; the identity travels with the id so
    // the engine can check they still belong together.
    adminLookupCustomer: (identity) =>
        get(`/admin/customers/lookup?identity=${encodeURIComponent(identity)}`)
            .catch((e) => { throw readable(e); }),

    adminDeleteCustomer: (customerId, identity) =>
        http.delete(`/admin/customers/${encodeURIComponent(customerId)}`
            + `?identity=${encodeURIComponent(identity)}`)
            .then((r) => r.data).catch((e) => { throw readable(e); }),

    adminDeletions: () => get('/admin/customers/deletions')
        .catch((e) => { throw readable(e); }),

    // -- TARS session -------------------------------------------------------------
    // The admin signs in on tars.rta.ae himself and pastes that request's Cookie
    // header. The engine keeps it, proves it works, and never hands the values back.
    tarsStatus: () => get('/tars/status').catch((e) => { throw readable(e); }),

    tarsImport: (cookie) => http.post('/tars/session', { cookie }).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    tarsForget: () => http.delete('/tars/session').then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    tarsProbe: () => http.post('/tars/probe').then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    // The engine signs in to TARS itself (the RTA account kept in The Engine, the code from the company Gmail).
    tarsSignIn: () => http.post('/tars/signin').then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    // -- registering Ejar's contracts on TARS (owner, 10 Oct 2026) -------------------
    // The engine signs in to TARS itself and fills its five screens - up to the summary, Terms on, Submit NOT pressed.
    tarsContracts: (days = 3, from = '', to = '') => get(`/tars/contracts?days=${encodeURIComponent(days)}`
        + (from || to ? `&date_from=${encodeURIComponent(from)}&date_to=${encodeURIComponent(to)}` : ''))
        .catch((e) => { throw readable(e); }),

    // Close on TARS: Ejar's closed contracts with their TARS status, and the close screen filled from Ejar's return
    tarsCloseContracts: (days = 1, from = '', to = '') => get(`/tars/close/contracts?days=${encodeURIComponent(days)}`
        + (from || to ? `&date_from=${encodeURIComponent(from)}&date_to=${encodeURIComponent(to)}` : ''))
        .catch((e) => { throw readable(e); }),

    tarsClose: (contractNo) => http.post('/tars/close', { contract_no: contractNo }).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    // odometer: the one typed on an earlier card (Ejar had none), kept for the register after a close-first
    tarsRegister: (contractNo, odometer = null) => http.post('/tars/register',
        odometer ? { contract_no: contractNo, odometer } : { contract_no: contractNo }).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    tarsRegisterProgress: (job, pictures = true) => get(`/tars/register/${encodeURIComponent(job)}?pictures=${pictures ? 1 : 0}`)
        .catch((e) => { throw readable(e); }),

    tarsRegisterCancel: (job) => http.post(`/tars/register/${encodeURIComponent(job)}/cancel`).then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    // Ejar has no pick-up odometer yet: register again with the one typed on the card (never below the car's last
    // reading on Ejar) - answers the new job
    tarsRegisterOdometer: (job, odometer) => http.post(`/tars/register/${encodeURIComponent(job)}/odometer`, { odometer })
        .then((r) => r.data)
        .catch((e) => { throw readable(e); }),

    // Register with the employee's own papers instead of the scans on the customer's Ejar profile
    tarsRegisterWithPapers: async (contractNo, papers = []) => {
        const form = new FormData();
        form.append('contract_no', contractNo);
        papers.forEach((file) => form.append('files', file, file.name));      // what each is, the engine tells
        try {
            const r = await http.post('/tars/register/with-papers', form);
            return r.data;
        } catch (e) { throw readable(e); }
    },

    // A customer new to TARS: the details Ejar lacks and the papers (a tourist's visit visa - a paper only)
    tarsRegisterCustomer: async (job, fields, papers = []) => {
        const form = new FormData();
        form.append('fields', JSON.stringify(fields || {}));
        papers.forEach((file) => form.append('files', file, file.name));
        try {
            const r = await http.post(`/tars/register/${encodeURIComponent(job)}/customer`, form);
            return r.data;
        } catch (e) { throw readable(e); }
    },
};

export default automationService;
